import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import CaptchaBox from '../components/auth/CaptchaBox';
import Field from '../components/ui/Field';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { register } from '../api/resources';
import { COUNTRIES, STATES, CITIES_BY_STATE } from '../utils/india';

function PasswordInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input className="input pr-12" type={show ? 'text' : 'password'} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted">
        {show ? '🙈' : '👁'}
      </button>
    </div>
  );
}

export default function Register() {
  const { login } = useAuth();
  const { success, error } = useToast();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', password: '', confirm: '',
    companyName: '', address: '', country: 'India', state: '', city: '', phone: '', companyEmail: '', gstin: '',
  });
  const [captcha1, setCaptcha1] = useState(null);
  const [captcha2, setCaptcha2] = useState(null);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateStep1 = () => {
    const e = {};
    if (!form.name) e.name = 'Name is required.';
    if (form.password.length < 8) e.password = 'Password must be at least 8 characters.';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
    if (!captcha1?.challengeId || !captcha1?.code) e.captcha = 'Enter the security code above.';
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.companyName) e.companyName = 'Company name is required.';
    if (!form.country) e.country = 'Country is required.';
    if (!form.state) e.state = 'State is required.';
    if (!form.city) e.city = 'City is required.';
    if (!captcha2?.challengeId || !captcha2?.code) e.captcha2 = 'Enter the security code above.';
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (validateStep1()) setStep(2);
      return;
    }
    if (!validateStep2()) return;
    setBusy(true);
    try {
      const data = await register({
        name: form.name, password: form.password, confirmPassword: form.confirm,
        companyName: form.companyName, address: form.address, country: form.country,
        state: form.state, city: form.city, phone: form.phone, companyEmail: form.companyEmail, gstin: form.gstin,
        challengeId: captcha2.challengeId, captchaCode: captcha2.code,
      });
      login(data.token, data.user);
      success(data.message || 'Account created successfully!');
      setTimeout(() => nav('/dashboard'), 700);
    } catch (x) {
      const msg = x.response?.data?.message || 'Registration failed. Please try again.';
      error(msg);
      setErrs({ form: msg });
    } finally {
      setBusy(false);
    }
  };

  const cities = CITIES_BY_STATE[form.state] || [];

  return (
    <AuthLayout title="Create Your Demo Account" tagline="Set up your company workspace in under a minute.">
      <div className="mb-4 flex items-center gap-2">
        {[1, 2].map((s) => (
          <div key={s} className={`flex-1 rounded-full px-3 py-1 text-center text-xs font-bold ${step === s ? 'bg-brand-700 text-white' : 'bg-surface-hover text-content-muted'}`}>
            Step {s} {s === 1 ? '· Personal Details' : '· Company Information'}
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl font-semibold text-brand-900">
        {step === 1 ? 'Your Details' : 'Company Information'}
      </h2>
      <p className="mt-1 text-sm text-content-muted">
        {step === 1 ? 'Tell us who you are.' : 'Where does your business operate?'}
      </p>

      {errs.form && <p className="mt-2 rounded-lg bg-danger-50 px-3 py-2 text-sm font-medium text-red-700">{errs.form}</p>}

      <form onSubmit={submit} className="mt-5 space-y-4">
        {step === 1 ? (
          <>
            <Field label="Name" required error={errs.name}>
              <input className="input" placeholder="Your name" value={form.name} onChange={set('name')} autoFocus />
            </Field>
            <Field label="Password" required error={errs.password}>
              <PasswordInput value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} placeholder="At least 8 characters" />
            </Field>
            <Field label="Confirm Password" required error={errs.confirm}>
              <PasswordInput value={form.confirm} onChange={(v) => setForm((f) => ({ ...f, confirm: v }))} placeholder="Re-enter password" />
            </Field>
            <CaptchaBox onCaptcha={setCaptcha1} error={errs.captcha} />
            <button type="submit" className="btn-primary w-full">
              Next →
            </button>
          </>
        ) : (
          <>
            <Field label="Company Name" required error={errs.companyName}>
              <input className="input" placeholder="e.g. Darby & Stitch Bespoke" value={form.companyName} onChange={set('companyName')} />
            </Field>
            <Field label="Address" error={errs.address}>
              <input className="input" placeholder="Street address" value={form.address} onChange={set('address')} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Country" required error={errs.country}>
                <select className="input" value={form.country} onChange={set('country')}>
                  {COUNTRIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="State" required error={errs.state}>
                <select className="input" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value, city: '' }))}>
                  <option value="">Select state</option>
                  {STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" required error={errs.city}>
                <select className="input" value={form.city} onChange={set('city')} disabled={!cities.length}>
                  <option value="">Select city</option>
                  {cities.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Phone Number" error={errs.phone}>
                <input className="input" placeholder="+91 …" value={form.phone} onChange={set('phone')} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company Email" error={errs.companyEmail}>
                <input className="input" type="email" placeholder="hello@company.com" value={form.companyEmail} onChange={set('companyEmail')} />
              </Field>
              <Field label="GST Number" error={errs.gstin}>
                <input className="input" placeholder="29ABCDE1234F1Z5" value={form.gstin} onChange={set('gstin')} />
              </Field>
            </div>
            <CaptchaBox onCaptcha={setCaptcha2} error={errs.captcha2} />
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(1)} className="btn-outline w-1/3">
                ‹ Back
              </button>
              <button type="submit" disabled={busy} className="btn-primary flex-1">
                {busy ? 'Creating…' : 'Create Account'}
              </button>
            </div>
          </>
        )}
      </form>

      <p className="mt-5 text-center text-sm text-content-muted">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-brand-700 hover:underline">
          Sign In
        </Link>
      </p>
    </AuthLayout>
  );
}